import axios from "axios";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { base_url } from "../../utils/axiosConfig";
// If corporate has separate thunk → change to e.g. registerCorporate

const CorporateSignup = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const [isRegistered, setIsRegistered] = useState(false);
  const timeoutRef = useRef(null);
  const [referredUserData, setReferredUserData] = useState(null);

  // Fetch referred user data
  const fetchReferredUserData = async (referralCode) => {
    if (!referralCode) {
      setReferredUserData(null);
      return;
    }
    try {
      const response = await axios.post(
        `${base_url}v1/user/verifyReferralCode`,
        {
          referralCode,
        },
      );
      setReferredUserData(response.data.data);
    } catch (error) {
      console.error("Error fetching referred user data:", error);
      setReferredUserData(null);
    }
  };

  // Debounce function for referral code validation
  const debounce = (func, delay) => {
    return (...args) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedFetchReferredUserData = debounce(fetchReferredUserData, 500);

  // Cleanup
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Validation schema – matches API required fields
  const validationSchema = Yup.object({
    corporateName: Yup.string().trim().required("Company name is required"),
    contactPerson: Yup.string()
      .trim()
      .required("Contact person name is required"),
    designation: Yup.string().trim().required("Designation is required"),
    contactNumber: Yup.string()
      .matches(/^[0-9]{10,15}$/, "Enter a valid phone number (10–15 digits)")
      .required("Contact number is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    companyType: Yup.string().required("Company Type is required"),
    address: Yup.object({
      address_line: Yup.string().trim().required("Street address is required"),
      city: Yup.string().trim().required("City is required"),
      district: Yup.string().trim().required("District is required"),
      state: Yup.string().trim().required("State is required"),
      country: Yup.string().trim().required("Country is required"),
      pincode: Yup.string()
        .matches(/^[0-9]{4,10}$/, "Enter a valid pincode")
        .required("Pincode is required"),
    }),
  });

  const initialValues = {
    corporateName: "",
    contactPerson: "",
    designation: "",
    contactNumber: "",
    email: "",
    companyType: "",
    profilePhoto: null,
    referralCode: "",
    address: {
      address_line: "",
      city: "",
      district: "",
      state: "",
      country: "",
      pincode: "",
    },
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
      <Modal.Body className="p-4 md:p-6">
        <div className="relative bg-white rounded-lg">
          <button
            onClick={onHide}
            className="absolute top-2 right-2 bg-transparent border-0"
          >
            <FaTimes size={16} color="#666" />
          </button>

          {!isRegistered ? (
            <>
              <div className="text-center mb-6">
                <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#00C70D] to-[#005AD0] bg-clip-text text-transparent">
                  Corporate / Industry Registration
                </h3>
                <p className="text-gray-500 text-sm">
                  Register your organization / company / Industry as a corporate
                  member
                </p>
              </div>

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                  try {
                    const formData = new FormData();

                    formData.append(
                      "corporateName",
                      values.corporateName.trim(),
                    );
                    formData.append(
                      "contactPerson",
                      values.contactPerson.trim(),
                    );
                    formData.append("designation", values.designation.trim());
                    formData.append(
                      "contactNumber",
                      values.contactNumber.trim(),
                    );
                    formData.append("companyType", values.companyType);
                    formData.append("email", values.email.trim().toLowerCase());

                    // Address – sent as stringified JSON (as your backend expects)
                    formData.append(
                      "address",
                      JSON.stringify({
                        address_line: values.address.address_line.trim(),
                        city: values.address.city.trim(),
                        district: values.address.district.trim(),
                        state: values.address.state.trim(),
                        country: values.address.country.trim(),
                        pincode: values.address.pincode.trim(),
                      }),
                    );

                    if (values.profilePhoto) {
                      formData.append("profilePhoto", values.profilePhoto);
                    }

                    if (referredUserData) {
                      formData.append("referredBy", referredUserData._id);
                    }

                    const response = await axios.post(
                      `${base_url}v1/corporate/corporateSignup`, // ← CHANGE THIS to your actual endpoint
                      formData,
                      {
                        headers: {
                          "Content-Type": "multipart/form-data",
                        },
                      },
                    );

                    if (response.data?.success) {
                      setIsRegistered(true);
                      resetForm();

                      setTimeout(() => {
                        onHide();
                        toast.success(
                          "Corporate registration successful! Please sign in.",
                        );
                      }, 1800);
                    } else {
                      throw new Error(
                        response.data?.message || "Registration failed",
                      );
                    }
                  } catch (error) {
                    console.error("Corporate registration error:", error);
                    toast.error(
                      error?.message ||
                        "Registration failed. Please try again.",
                    );
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {({ isSubmitting, setFieldValue, values }) => (
                  <Form className="flex flex-col gap-4">
                    {/* Company & Contact Person */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div>
                        <label className="block text-xs md:text-xs font-semibold">
                          Company/Industry Name
                          <span className="text-red-500">*</span>
                        </label>
                        <Field
                          type="text"
                          name="corporateName"
                          placeholder="Enter company name"
                          className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                        <ErrorMessage
                          name="corporateName"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-xs md:text-xs font-semibold">
                          Contact Person Name
                          <span className="text-red-500">*</span>
                        </label>
                        <Field
                          type="text"
                          name="contactPerson"
                          placeholder="Full name of contact person"
                          className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                        <ErrorMessage
                          name="contactPerson"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-xs md:text-xs font-semibold">
                          Industry Type<span className="text-red-500">*</span>
                        </label>
                        <Field
                          as="select"
                          name="companyType"
                          className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                        >
                          <option value="">Select Type</option>
                          <option value="education">Education Sector</option>
                          <option value="healthcare">Healthcare Sector</option>
                          <option value="manufacturing">
                            Manufacturing / Factory
                          </option>
                          <option value="corporate">
                            Corporate / IT Sector
                          </option>
                          <option value="government">Government Sector</option>
                          <option value="hospitality">
                            Hospitality Sector
                          </option>
                          <option value="retail">
                            Retail & Wholesale Sector
                          </option>
                          <option value="logistics">
                            Logistics & Transport
                          </option>
                          <option value="construction">
                            Construction & Real Estate
                          </option>
                          <option value="finance">Financial Services</option>
                          <option value="ngo">NGO / Non-Profit</option>
                          <option value="defense">Defense & Security</option>
                          <option value="agriculture">
                            Agriculture Sector
                          </option>
                          <option value="other">Other</option>
                        </Field>
                        <ErrorMessage
                          name="companyType"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-xs md:text-xs font-semibold">
                          Designation<span className="text-red-500">*</span>
                        </label>
                        <Field
                          type="text"
                          name="designation"
                          placeholder="e.g. HR Manager, Director"
                          className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                        <ErrorMessage
                          name="designation"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-xs md:text-xs font-semibold">
                          Contact Number<span className="text-red-500">*</span>
                        </label>
                        <Field
                          type="text"
                          name="contactNumber"
                          placeholder="10-digit mobile number"
                          className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                        <ErrorMessage
                          name="contactNumber"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-xs md:text-xs font-semibold">
                          Official Email<span className="text-red-500">*</span>
                        </label>
                        <Field
                          type="email"
                          name="email"
                          placeholder="company@example.com"
                          className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                        <ErrorMessage
                          name="email"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>
                    </div>

                    {/* Address Fields */}
                    <div className="mt-2">
                      <h4 className="text-base font-semibold mb-3">
                        Registered Office Address
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            Street Address / Building
                            <span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="address.address_line"
                            placeholder="Flat no, Building name, Street"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <ErrorMessage
                            name="address.address_line"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>

                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            City<span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="address.city"
                            placeholder="Mumbai, Delhi, etc."
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <ErrorMessage
                            name="address.city"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>

                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            District<span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="address.district"
                            placeholder="e.g. Mumbai City, Thane"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <ErrorMessage
                            name="address.district"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>

                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            State<span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="address.state"
                            placeholder="Maharashtra, Karnataka, etc."
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <ErrorMessage
                            name="address.state"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>

                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            Country<span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="address.country"
                            placeholder="India"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <ErrorMessage
                            name="address.country"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>

                        <div>
                          <label className="block text-xs md:text-xs font-semibold">
                            Pincode<span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="text"
                            name="address.pincode"
                            placeholder="400001"
                            className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <ErrorMessage
                            name="address.pincode"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {/* Profile Photo */}
                      <div>
                        <label className="block text-xs md:text-xs font-semibold">
                          Industry Logo / Contact Person Photo
                          <span className="text-red-500">*</span>
                        </label>
                        <button
                          type="button"
                          className="w-full mt-1 p-2 bg-[#00C70D] text-white rounded-lg text-sm font-medium"
                          onClick={() =>
                            document.getElementById("profilePhoto").click()
                          }
                        >
                          Upload Photo
                        </button>
                        <input
                          type="file"
                          id="profilePhoto"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) setFieldValue("profilePhoto", file);
                          }}
                        />
                        {values.profilePhoto && (
                          <div className="mt-2 text-xs text-green-700 font-medium">
                            Selected: {values.profilePhoto.name}
                          </div>
                        )}
                        <ErrorMessage
                          name="profilePhoto"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>
                      <div>
                        <label className="block text-xs md:text-xs font-semibold">
                          Referral Code
                        </label>
                        <Field
                          type="text"
                          name="referralCode"
                          placeholder="Enter Referral Code"
                          className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                          onChange={(e) => {
                            setFieldValue("referralCode", e.target.value);
                            debouncedFetchReferredUserData(e.target.value);
                          }}
                        />
                        <ErrorMessage
                          name="referralCode"
                          component="div"
                          className="text-red-500 text-xs mt-1"
                        />
                      </div>
                    </div>
                    {referredUserData ? (
                      <>
                        <h2 className="text-lg md:text-xl font-medium">
                          Upline Information
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs md:text-xs font-semibold">
                              Full Name
                            </label>
                            <input
                              type="text"
                              value={referredUserData.name || ""}
                              readOnly
                              className="modern-input w-full mt-1 p-2.5  text-sm border-gray-300 bg-gray-100"
                            />
                          </div>
                          <div>
                            <label className="block text-xs md:text-xs font-semibold">
                              Phone Number
                            </label>
                            <input
                              type="text"
                              value={
                                referredUserData.mobile ||
                                referredUserData.phone ||
                                ""
                              }
                              readOnly
                              className="modern-input w-full mt-1 p-2.5  text-sm border-gray-300 bg-gray-100"
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {values.referralCode && (
                          <div className="text-red-500 text-xs mt-1">
                            Invalid Referral Code
                          </div>
                        )}
                      </>
                    )}

                    <div className="mt-4 flex justify-center">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-44 py-2.5 bg-[#00C70D] text-white font-semibold rounded-lg shadow-md flex items-center justify-center ${
                          isSubmitting
                            ? "opacity-70 cursor-not-allowed"
                            : "hover:bg-[#00b50c]"
                        }`}
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                              />
                            </svg>
                            Registering...
                          </>
                        ) : (
                          "Register"
                        )}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </>
          ) : (
            <div className="text-center py-10">
              <h2 className="text-2xl font-bold text-green-600 mb-3">
                Registration Successful
              </h2>
              <svg
                className="w-16 h-16 mx-auto text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <p className="mt-4 text-gray-600">
                You will be redirected shortly...
              </p>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CorporateSignup;
